import { CREDENTIAL_TYPE_ITEMS } from './experienceTypeOptions';

export const EDUCATION_LEVEL_OPTIONS = [
  {
    id: 1,
    // Backend EducationLevelEnum: "Less than High School" maps to lessThanHighSchool.
    value: 'lessThanHighSchool',
    labelId: 'forms.onboarding.education.noDiploma',
    dropdownId: 'education-less-than-hs-dropdown',
  },
  { id: 2, value: 'ged', labelId: 'forms.onboarding.education.ged', dropdownId: 'education-ged-dropdown' },
  {
    id: 3,
    value: 'highSchoolDiploma',
    labelId: 'forms.onboarding.education.highSchoolDiploma',
    dropdownId: 'education-diploma-dropdown',
  },
  {
    id: 5,
    value: 'someCollege',
    labelId: 'forms.onboarding.education.someCollege',
    dropdownId: 'education-some-college-dropdown',
  },
  {
    id: 6,
    value: 'certificateProficiency',
    labelId: 'forms.onboarding.education.certificateProficiency',
    dropdownId: 'education-certificate-proficiency-dropdown',
  },
  {
    id: 7,
    value: 'technicalCertificate',
    labelId: 'forms.onboarding.education.technicalCertificate',
    dropdownId: 'education-technical-certificate-dropdown',
  },
  {
    id: 8,
    value: 'assocDegree',
    labelId: 'forms.onboarding.education.assocDegree',
    dropdownId: 'education-associates-dropdown',
  },
  {
    id: 9,
    value: 'advancedCertificate',
    labelId: 'forms.onboarding.education.advancedCertificate',
    dropdownId: 'education-advanced-certificate-dropdown',
  },
  {
    id: 10,
    value: 'bachDegree',
    labelId: 'forms.onboarding.education.bachDegree',
    dropdownId: 'education-bachelors-dropdown',
  },
  {
    id: 11,
    value: 'postBachCertificate',
    labelId: 'forms.onboarding.education.postBachelorsCertificate',
    dropdownId: 'education-post-bachelors-certificate-dropdown',
  },
  {
    id: 12,
    value: 'mastersDegree',
    labelId: 'forms.onboarding.education.mastersDegree',
    dropdownId: 'education-masters-dropdown',
  },
  {
    id: 13,
    // Backend EducationLevelEnum: "Graduate Certificate" maps to graduateCertificate.
    value: 'graduateCertificate',
    labelId: 'forms.onboarding.education.graduateCertificate',
    dropdownId: 'education-graduate-certificate-dropdown',
  },
  {
    id: 14,
    value: 'doctoralProfessional',
    labelId: 'forms.onboarding.education.doctoralProfessional',
    dropdownId: 'education-doctoral-professional-dropdown',
  },
  {
    id: 15,
    value: 'doctoralResearch',
    labelId: 'forms.onboarding.education.doctoralResearch',
    dropdownId: 'education-doctoral-research-dropdown',
  },
];

// Entries removed from EDUCATION_LEVEL_OPTIONS (not valid degree levels) but kept
// here so EDUCATION_LEVEL_BY_ID can still display records saved before the reclassification.
const REMOVED_LEVEL_OPTIONS = [
  { id: 4, value: 'someHighSchool', labelId: 'forms.onboarding.education.someHighSchool' },
  { id: 18, value: 'apperenticeshipRTI', labelId: 'experience.form.apprenticeshipRTIType' },
];

// Lookup by the numeric id that is persisted in education[].degree.
export const EDUCATION_LEVEL_BY_ID = new Map(
  [...EDUCATION_LEVEL_OPTIONS, ...REMOVED_LEVEL_OPTIONS].map((o) => [o.id, o])
);

// Options for the Add Education "Select an educational experience" dropdown.
// Keyed `degree|<id>` so the existing TypeSelect split logic keeps working
// (type = 'degree', level = the numeric id after the pipe). The backend stores
// the level as a number in education[].degree, which matches these ids.
export const EDUCATION_LEVEL_ITEMS = EDUCATION_LEVEL_OPTIONS.filter((o) => o.value !== 'lessThanHighSchool').map(
  (o) => ({
    key: `degree|${o.id}`,
    labelId: o.labelId,
  })
);

// Shown in the education dropdown only for users who have indicated military service.
// Key is a plain string (no degree| prefix) so TypeSelect sets type='militaryCourse' and level=null.
export const MILITARY_COURSE_ITEM = {
  key: 'militaryCourse',
  labelId: 'experience.form.militaryCourseType',
};

// Shown alongside MILITARY_COURSE_ITEM for users with military service.
export const MILITARY_EXPERIENCE_ITEM = {
  key: 'militaryExperience',
  labelId: 'experience.form.militaryExperienceType',
};

// The full "Education Experience/Credentials" list, shared by the Add Education
// form and the JST Academic Degrees/Certificates section so both offer exactly
// the same choices: Military Course first, then credential types, then education
// levels (Less than High School excluded — it is not a credential).
export const EDUCATION_CREDENTIAL_ITEMS = [
  MILITARY_COURSE_ITEM,
  ...CREDENTIAL_TYPE_ITEMS,
  ...EDUCATION_LEVEL_OPTIONS.filter((o) => o.value !== 'lessThanHighSchool').map((o) => ({
    key: o.value,
    labelId: o.labelId,
  })),
];

// Stored value → labelId. Anything rendering a credential must go through this so
// the raw enum ('assocDegree') never reaches the screen instead of its label
// ("Associate Degree"). First entry wins if two lists ever share a key.
export const EDUCATION_CREDENTIAL_LABEL_IDS = EDUCATION_CREDENTIAL_ITEMS.reduce((acc, { key, labelId }) => {
  if (!(key in acc)) acc[key] = labelId;
  return acc;
}, {});

// Legacy highest_education_level string values that were renamed to match the
// backend EducationLevelEnum. Applied on read so records saved under the old
// values still pre-select the correct option.
export const LEGACY_HIGHEST_EDUCATION_MAP = {
  noDiploma: 'lessThanHighSchool',
  postMastersCertificate: 'graduateCertificate',
};

/** Map a stored highest_education_level to its current enum value (legacy-safe). */
export function normalizeHighestEducationValue(value) {
  if (value == null) return value;
  const num = Number(value);
  if (!Number.isNaN(num) && (typeof value === 'number' || String(num) === String(value))) {
    const legacyId = Object.prototype.hasOwnProperty.call(LEGACY_DEGREE_MAP, num)
      ? LEGACY_DEGREE_MAP[num]
      : EDUCATION_LEVEL_BY_ID.has(num)
      ? num
      : null;
    return legacyId != null ? EDUCATION_LEVEL_BY_ID.get(legacyId)?.value ?? null : null;
  }
  return LEGACY_HIGHEST_EDUCATION_MAP[value] ?? value;
}

// Legacy "years-of-education" degree codes (the old onboarding/profile scheme)
// mapped to current option ids, for displaying old users' data. These are NOT
// added to the dropdown — new users only ever pick from EDUCATION_LEVEL_ITEMS.
// Mappings confirmed with the client (16.5 -> Graduate Certificate, 17.5 ->
// Master's, 20 -> Doctoral-Professional).
export const LEGACY_DEGREE_MAP = {
  11: 1, // Less than High School -> noDiploma
  11.5: 4, // Some high school -> someHighSchool
  12: 3, // HS diploma or equivalent -> highSchoolDiploma
  13: 5, // Some college -> someCollege
  14: 8, // Associate's -> assocDegree
  16: 10, // Bachelor's -> bachDegree
  16.5: 13, // Some graduate school -> Graduate Certificate
  17: 12, // Master's -> mastersDegree
  17.5: 12, // (legacy) -> Master's
  20: 14, // Doctoral/professional -> Doctoral-Professional
};

/**
 * Resolve a stored education[].degree value to a current option id (1-15), or
 * null if unknown.
 *
 * Disambiguation by `type`: records saved by the current dropdown store a valid
 * id (1-15) together with type 'degree', so a stored `12` there means Master's
 * (id 12). Older records store an 11-20 "years" code with no such type, so a
 * stored `12` there means High School Diploma — translated via LEGACY_DEGREE_MAP.
 */
export function resolveStoredDegreeToId(degree, type) {
  if (degree == null || degree === '') return null;
  const num = Number(degree);
  if (Number.isNaN(num)) return null;
  // Current record: the value already is a valid option id.
  if (type === 'degree' && EDUCATION_LEVEL_BY_ID.has(num)) return num;
  // Legacy "years" code → current id.
  if (Object.prototype.hasOwnProperty.call(LEGACY_DEGREE_MAP, num)) return LEGACY_DEGREE_MAP[num];
  // Fallback: a bare valid id (e.g. from a record missing its type).
  return EDUCATION_LEVEL_BY_ID.has(num) ? num : null;
}
